import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { useArticle } from "@/hooks/useCatalogue";
import { BrandLogo } from "@/components/BrandLogo";
import Reveal from "@/components/Reveal";
import ShareRow from "@/components/ShareRow";
import NotFound from "@/pages/NotFound";

function ReadingProgress({ target }) {
  const [p, setP] = useState(0);
  useEffect(() => {
    const onScroll = () => {
      const el = target.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const total = rect.height - window.innerHeight * 0.6;
      const done = Math.min(Math.max(-rect.top + window.innerHeight * 0.25, 0), Math.max(total, 1));
      setP(Math.round((done / Math.max(total, 1)) * 100));
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => { window.removeEventListener("scroll", onScroll); window.removeEventListener("resize", onScroll); };
  }, [target]);
  return (
    <div
      className="fixed left-0 right-0 top-0 z-[60] h-[3px] bg-transparent"
      role="progressbar"
      aria-label="Reading progress"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={p}
      data-testid="reading-progress"
    >
      <div className="h-full bg-champagne origin-left transition-transform duration-150 ease-out" style={{ transform: `scaleX(${p / 100})` }} />
    </div>
  );
}

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });

function Block({ block, first }) {
  if (block.type === "heading") {
    return <h2 className="font-serif font-light text-3xl sm:text-4xl text-forest mt-16 mb-6">{block.text}</h2>;
  }
  if (block.type === "quote") {
    return (
      <figure className="pull-quote my-14 lg:-mx-16">
        <blockquote className="font-serif font-light italic text-3xl sm:text-4xl leading-[1.2] text-forest text-balance">
          {block.text}
        </blockquote>
        {block.cite && <figcaption className="mt-5 text-[11px] uppercase tracking-[0.24em] text-champagne">— {block.cite}</figcaption>}
      </figure>
    );
  }
  return (
    <p className={`text-lg sm:text-xl leading-[1.75] text-charcoal/80 font-light mb-7 ${first ? "drop-cap" : ""}`} data-testid={first ? "article-first-paragraph" : undefined}>
      {block.text}
    </p>
  );
}

export default function JournalArticle() {
  const { slug } = useParams();
  const { data: a, isLoading, isError } = useArticle(slug);
  const bodyRef = useRef(null);

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  if (isLoading) return <div className="max-w-3xl mx-auto px-4 py-32 text-center text-sm uppercase tracking-[0.24em] text-charcoal/50">Opening…</div>;
  if (isError || !a) return <NotFound />;

  const firstParagraph = a.body.findIndex((b) => b.type === "paragraph");

  return (
    <article className="relative" data-testid="journal-article">
      <ReadingProgress target={bodyRef} />

      <header className="relative overflow-hidden border-b border-forest/10">
        <BrandLogo variant="mark" className="pointer-events-none select-none absolute -right-24 -top-16 w-[480px] opacity-[0.05]" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 sm:pt-20 pb-14">
          <Link to="/journal" className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-charcoal/55 hover:text-forest transition-colors" data-testid="article-back-link">
            <ArrowLeft size={14} /> The Journal
          </Link>
          <div className="mt-10"><p className="eyebrow">{a.category}</p></div>
          <h1 className="mt-6 font-serif font-light text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[0.95] text-forest text-balance" data-testid="article-title">
            {a.title}
          </h1>
          <p className="mt-8 text-lg sm:text-xl leading-relaxed text-charcoal/65 font-light max-w-2xl">{a.excerpt}</p>
          <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-2 text-[11px] uppercase tracking-[0.22em] text-charcoal/50">
            <span>{a.author}</span>
            <span className="w-8 h-px bg-champagne" />
            <span>{formatDate(a.created_at)}</span>
            <span className="w-8 h-px bg-champagne" />
            <span>{a.read_minutes} min read</span>
          </div>
        </div>
      </header>

      {a.image && (
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 mt-14">
          <div className="frame"><div className="aspect-[16/9] overflow-hidden bg-linen"><img src={a.image} alt="" className="w-full h-full object-cover" /></div></div>
        </div>
      )}

      <div ref={bodyRef} className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-20" data-testid="article-body">
        {a.body.map((b, i) => <Block key={i} block={b} first={i === firstParagraph} />)}
        <div className="mt-16 flex items-center gap-6">
          <span className="h-px flex-1 bg-forest/15" />
          <BrandLogo variant="mark" className="h-8 w-auto opacity-70" />
          <span className="h-px flex-1 bg-forest/15" />
        </div>
        <div className="mt-10 flex justify-center">
          <ShareRow title={a.title} excerpt={a.excerpt} />
        </div>
        {a.sample && (
          <p className="mt-8 text-center text-[11px] uppercase tracking-[0.2em] text-charcoal/40" data-testid="article-sample-note">
            Sample editorial — to be replaced by ELA's own writing
          </p>
        )}
      </div>

      {a.related?.length > 0 && (
        <section className="bg-linen border-t border-forest/10">
          <Reveal className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
            <p className="eyebrow mb-8">Continue reading</p>
            <div className="grid md:grid-cols-2 gap-6">
              {a.related.map((r) => (
                <Link key={r.slug} to={`/journal/${r.slug}`} data-testid={`related-article-${r.slug}`} className="group border border-forest/15 bg-cream p-8 hover:border-forest/50 transition-colors">
                  <p className="text-[11px] uppercase tracking-[0.22em] text-champagne">{r.category} · {r.read_minutes} min</p>
                  <h3 className="mt-3 font-serif text-2xl sm:text-3xl text-forest font-light leading-tight">{r.title}</h3>
                  <p className="mt-3 text-sm text-charcoal/65 font-light">{r.excerpt}</p>
                  <span className="mt-6 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-forest link-underline">Read <ArrowRight size={13} className="transition-transform group-hover:translate-x-1" /></span>
                </Link>
              ))}
            </div>
          </Reveal>
        </section>
      )}
    </article>
  );
}
